import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { formatCount, checkIsLiked, likeProject, trackProjectView } from "../utils/deviceTracker";
import "./ProjectStatsBadge.css";

const ProjectStatsBadge = ({ docId, trackView = false }) => {
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isPopping, setIsPopping] = useState(false);

  useEffect(() => {
    if (!docId) return;

    // 1. Track view ONLY when trackView is true (e.g. on Project Details page)
    if (trackView) {
      trackProjectView(docId);
    }

    // 2. Check if current device already liked this project
    checkIsLiked(docId).then((liked) => {
      setIsLiked(liked);
    });

    // 3. Listen to real-time project statistics from Firestore
    const projectRef = doc(db, "projects", docId);
    const unsubscribe = onSnapshot(projectRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setLikes(data.likes || 0);
        setViews(data.views || 0);
      }
    }, (error) => {
      console.error(`Error listening to stats for ${docId}:`, error);
    });

    return () => unsubscribe();
  }, [docId, trackView]);

  const handleLike = async () => {
    if (isLiked) return; // Prevent duplicate likes on same device

    // Optimistic UI update
    setIsLiked(true);
    setLikes((prev) => prev + 1);
    setIsPopping(true);
    setTimeout(() => setIsPopping(false), 400);

    // Persist to Firestore
    const result = await likeProject(docId);
    if (!result.success && result.alreadyLiked) {
      setIsLiked(true);
    }
  };

  if (!docId) return null;

  return (
    <div className="project-stats-badge-container">
      <button
        className={`stat-pill like-pill ${isLiked ? "liked" : ""} ${isPopping ? "pop" : ""}`}
        onClick={handleLike}
        disabled={isLiked}
        title={isLiked ? "You already liked this project" : "Click to like project"}
      >
        <span className="heart-icon">{isLiked ? "❤️" : "🤍"}</span>
        <span className="stat-label">{formatCount(likes)} Likes</span>
      </button>

      <div className="stat-pill views-pill" title="Project Views">
        <span className="eye-icon">👀</span>
        <span className="stat-label">{formatCount(views)} Views</span>
      </div>
    </div>
  );
};

export default ProjectStatsBadge;
